import ConfirmModal from "PFComponents/confirm/confirm_modal";
import { Icon } from "PFComponents/icon";
import AutoSelect from "PFComponents/select/autoselect";
import { Typography } from "PFComponents/typography";
import { fetchProfiles } from "PFCore/services/profile";
import { IMatchScores, Profile } from "PFTypes";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { ProfileItem } from "../../../components/profile_item";
import { BookingsToReassignSelector } from "./bookings_to_reassign_selector";
import { MatchScoreSection } from "./match_score_section";
import { ProfileItemDisplay } from "./profile_item_display";
import css from "./reassign_booking_modal.module.scss";

export enum ReassignMode {
  Custom = "custom",
  All = "all"
}

type SelectedUserData = {
  profile: Profile;
  scores?: IMatchScores;
};

type ReassignBookingModalProps = {
  mode: ReassignMode;
  roleName: string;
  profileFrom: Profile;
  profileTo?: SelectedUserData;
  readOnlyMode?: boolean;
  roleBookingsIds: number[];
  reassignOptionSelected: string;
  setReassignOptionSelected: React.Dispatch<React.SetStateAction<string>>;
  onClose?: () => void;
  onSubmit: () => Promise<void>;
};

export const ReassignBookingModal = ({
  mode,
  roleName,
  profileFrom,
  profileTo,
  readOnlyMode,
  roleBookingsIds,
  reassignOptionSelected,
  setReassignOptionSelected,
  onSubmit,
  onClose
}: ReassignBookingModalProps) => {
  const { t } = useTranslation("bookingModule");
  const { t: translationT } = useTranslation();
  const [selectedUser, setSelectedUser] = useState<SelectedUserData | null>(profileTo ?? null);

  const title =
    mode === ReassignMode.Custom
      ? t("bookings.reassign.title", { roleName })
      : t("bookings.reassign.titleAll", { roleName });

  const shouldDisplayBookingsToReassignSelector = mode === ReassignMode.Custom && roleBookingsIds.length > 1;

  // TODO: [PROF-3444] Display matching scores section when the API/HAL part is ready
  const displayMatchingScoreSection = false;

  return (
    <ConfirmModal
      title={title}
      labelOK={t("bookings.reassign.reassignAction")}
      labelCancel={translationT("cancel")}
      handleOK={onSubmit}
      handleClose={onClose}
      style={{ maxWidth: 715 }}
    >
      <div className={css.root}>
        <Typography variant="bodyRegular">
          {mode === ReassignMode.Custom
            ? t("bookings.reassign.question")
            : t("bookings.reassign.questionAll", { count: roleBookingsIds.length })}
        </Typography>
        {shouldDisplayBookingsToReassignSelector && (
          <BookingsToReassignSelector
            roleBookingsIds={roleBookingsIds}
            reassignOptionSelected={reassignOptionSelected}
            setReassignOptionSelected={setReassignOptionSelected}
          />
        )}
        <div className={css.reassignContainer}>
          <ProfileItemDisplay profile={profileFrom} />
          <Icon name="reassign" />
          <div className={css.personToContainer}>
            <div className={css.personTo}>
              <AutoSelect
                displayValues={selectedUser ? <ProfileItemDisplay profile={selectedUser.profile} /> : null}
                rootClassName={css.selectInput}
                multi={false}
                disabled={readOnlyMode}
                closeOnChange
                letClear={mode === ReassignMode.All}
                values={selectedUser ? [selectedUser.profile] : null}
                query={(term) => {
                  const filters = term ? { fields: { term: term } } : {};
                  return fetchProfiles({ filters, page: 1, per_page: 20, full: true });
                }}
                formatOption={({ scores, profile, profiles, displayElement, search_id, total }) => ({
                  id: profile?.id,
                  displayElement: <ProfileItem profile={profile} />,
                  item: profile ? { profile, scores } : { profiles, displayElement, search_id, total }
                })}
                handleChange={(user) => {
                  setSelectedUser(user[0]);
                }}
                parseResponse={(resp) => (Array.isArray(resp.entries) ? resp.entries : resp)}
                required
              />
            </div>
            {displayMatchingScoreSection && (
              <div className={css.matchScoresSection}>
                {selectedUser && <MatchScoreSection scores={selectedUser.scores} />}
              </div>
            )}
          </div>
        </div>
      </div>
    </ConfirmModal>
  );
};
